import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Seo from '../components/Seo';
import ProductCard from '../components/ProductCard';
import TrustBar from '../components/TrustBar';
import PillarIcon from '../components/PillarIcon';
import { api } from '../lib/api';
import { brand, categories, enterprisePillars } from '../config/brand';

type IconProps = { className?: string };

const svgBase = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function AtomIcon({ className }: IconProps) {
  return (
    <svg {...svgBase} className={className}>
      <circle cx="12" cy="12" r="1" />
      <path d="M20.2 20.2c2.04-2.03.02-7.36-4.5-11.9-4.54-4.52-9.87-6.54-11.9-4.5-2.04 2.03-.02 7.36 4.5 11.9 4.54 4.52 9.87 6.54 11.9 4.5Z" />
      <path d="M15.7 15.7c4.52-4.54 6.54-9.87 4.5-11.9-2.03-2.04-7.36-.02-11.9 4.5-4.52 4.54-6.54 9.87-4.5 11.9 2.03 2.04 7.36.02 11.9-4.5Z" />
    </svg>
  );
}

function FlaskIcon({ className }: IconProps) {
  return (
    <svg {...svgBase} className={className}>
      <path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2" />
      <path d="M6.453 15h11.094" />
      <path d="M8.5 2h7" />
    </svg>
  );
}

function AwardIcon({ className }: IconProps) {
  return (
    <svg {...svgBase} className={className}>
      <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526" />
      <circle cx="12" cy="8" r="6" />
    </svg>
  );
}

function HandshakeIcon({ className }: IconProps) {
  return (
    <svg {...svgBase} className={className}>
      <path d="m11 17 2 2a1 1 0 1 0 3-3" />
      <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
      <path d="m21 3 1 11h-2" />
      <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
      <path d="M3 4h8" />
    </svg>
  );
}

function ShieldCheckIcon({ className }: IconProps) {
  return (
    <svg {...svgBase} className={className}>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

const heroFeatures = [
  { label: 'Advanced Formulations', Icon: AtomIcon },
  { label: 'Scientific Excellence', Icon: FlaskIcon },
  { label: 'Premium Quality', Icon: AwardIcon },
  { label: 'Trusted Partner', Icon: HandshakeIcon },
];

const whyChoose = [
  {
    title: 'Scientifically engineered performance',
    body: 'Advanced formulations deliver powerful cleaning, superior hygiene, and consistent results across demanding commercial environments.',
    Icon: FlaskIcon,
  },
  {
    title: 'Trusted quality for professional facilities',
    body: 'Designed for hotels, hospitals, offices, educational institutions, and industrial facilities where cleanliness, safety, and reliability are non-negotiable.',
    Icon: ShieldCheckIcon,
  },
  {
    title: 'Reliable manufacturing & long-term partnership',
    body: 'Consistent product quality, dependable supply, and dedicated customer support make UCLIPSE a trusted partner for businesses committed to excellence.',
    Icon: HandshakeIcon,
  },
];

const industries = [
  {
    title: 'Hotels & hospitality',
    body: 'Lobbies, guest rooms, and banquet spaces that stay flawless from check-in to check-out.',
    img: '/home/hotel.jpg',
    alt: 'Sparkling clean luxury hotel lobby with polished marble floors',
  },
  {
    title: 'Commercial kitchens & F&B',
    body: 'Heavy-duty degreasing and food-safe sanitation for high-volume stainless-steel kitchens.',
    img: '/home/kitchen.jpg',
    alt: 'Spotless commercial stainless-steel kitchen with cookware',
  },
  {
    title: 'Hospitals & laboratories',
    body: 'Hospital-grade hygiene and residue-free surfaces for clinical and laboratory environments.',
    img: '/home/medical-lab.jpg',
    alt: 'Sterile medical laboratory with microscopes and clean workstations',
  },
  {
    title: 'Corporate & facilities',
    body: 'Reception areas, offices, and shared spaces that make the right first impression.',
    img: '/home/lobby.jpg',
    alt: 'Clean corporate lobby with UCLIPSE branding at the reception desk',
  },
];

export default function HomePage() {
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: api.listProducts,
  });

  const featured = products.filter((p) => p.featured && p.active).slice(0, 6);
  const fallback = products.filter((p) => p.active).slice(0, 6);
  const showcase = featured.length > 0 ? featured : fallback;

  const orgJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: brand.legalEntity,
      alternateName: 'Uclipse',
      url: 'https://www.uclipse.com/',
      logo: 'https://www.uclipse.com/favicon.svg',
      description: brand.description,
      foundingDate: '2008',
      slogan: brand.tagline,
      award: 'ZED Silver Certification under the MSME Sustainable (ZED) Certification Scheme, Government of India',
      address: {
        '@type': 'PostalAddress',
        streetAddress: `${brand.address.line1}, ${brand.address.line2}`,
        addressLocality: brand.address.city,
        addressRegion: brand.address.state,
        postalCode: brand.address.postalCode,
        addressCountry: 'IN',
      },
      email: brand.email,
      telephone: brand.phone,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: brand.phone,
        email: brand.email,
        contactType: 'sales',
        areaServed: 'IN',
        availableLanguage: ['en', 'hi'],
      },
      knowsAbout: [
        'Institutional cleaning concentrates',
        'Kid-safe home care',
        'Bioenzyme cleaning products',
        'Baby liquid detergent',
        'Fabric softener',
        'Private label manufacturing',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Uclipse',
      url: 'https://www.uclipse.com/',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.uclipse.com/products?search={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
  ];

  return (
    <>
      <Seo title="Industrial Cleaning Concentrates" description={brand.description} jsonLd={orgJsonLd} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%230E5BA8' fill-opacity='0.05'%3E%3Ccircle cx='24' cy='24' r='2'/%3E%3C/g%3E%3C/svg%3E\")",
          }}
        />
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand-100/60 blur-3xl" />
        <div className="container-page relative grid items-center gap-10 py-14 md:grid-cols-2 md:py-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-700 ring-1 ring-brand-100">
              ZED Silver Certified · MSME India
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold uppercase leading-[1.05] tracking-tight text-brand-800 sm:text-5xl lg:text-[3.4rem]">
              Engineered for<br />
              <span className="text-brand-500">superior hygiene.</span><br />
              Trusted for<br />
              <span className="text-brand-500">professional performance.</span>
            </h1>
            <div className="mt-6 flex items-start gap-3">
              <span className="mt-2 h-0.5 w-10 shrink-0 bg-brand-500" />
              <p className="font-display text-sm font-semibold uppercase tracking-wide text-brand-700 sm:text-base">
                Premium cleaning solutions for hotels, hospitals, industries &amp; commercial facilities
              </p>
            </div>
            <p className="mt-5 max-w-xl text-slate-600">
              At <strong className="text-slate-800">UCLIPSE Detergents &amp; Chemicals</strong>, we deliver
              high-performance cleaning and hygiene solutions that ensure spotless environments, safety, and
              efficiency you can rely on — every single day.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/distributors" className="btn-accent">
                Become a Distributor
              </Link>
              <Link to="/products" className="btn-secondary">
                Browse Products
              </Link>
            </div>
            <div className="mt-10 grid max-w-lg grid-cols-2 gap-x-6 gap-y-6 border-t border-slate-100 pt-8 sm:grid-cols-4">
              {heroFeatures.map((f) => (
                <div key={f.label} className="flex flex-col items-center text-center sm:items-start sm:text-left">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
                    <f.Icon className="h-6 w-6" />
                  </span>
                  <span className="mt-2 text-[11px] font-semibold uppercase leading-tight tracking-wide text-slate-700">
                    {f.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="pointer-events-none absolute -inset-3 -z-10 rounded-[2rem] bg-gradient-to-br from-brand-500/20 via-brand-300/10 to-transparent blur-2xl" />
            <div className="relative overflow-hidden rounded-3xl shadow-2xl ring-1 ring-slate-200">
              <img
                src="/home/lobby.jpg"
                alt="UCLIPSE-branded corporate lobby kept spotless with institutional cleaning solutions"
                className="h-full w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-brand-900/40 to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-2xl bg-white p-4 shadow-xl ring-1 ring-slate-100 sm:block">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-accent-green/10 text-accent-green">
                  <ShieldCheckIcon className="h-6 w-6" />
                </span>
                <div>
                  <div className="font-display text-sm font-bold text-slate-900">Trusted hygiene</div>
                  <div className="text-xs text-slate-500">Hotels · Hospitals · Industry</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust credentials */}
      <TrustBar variant="band" />

      {/* About + Why choose UCLIPSE */}
      <section className="bg-slate-50">
        <div className="grid md:grid-cols-2">
          <div className="relative overflow-hidden px-4 py-14 sm:px-6 md:py-20 lg:px-12">
            <div
              className="pointer-events-none absolute inset-0 opacity-70"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg width='56' height='100' viewBox='0 0 56 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M28 66L0 50V16l28-16 28 16v34L28 66zM28 0v16M0 50l28 16 28-16' fill='none' stroke='%230E5BA8' stroke-opacity='0.06'/%3E%3C/svg%3E\")",
              }}
            />
            <div className="relative mx-auto max-w-xl md:ml-auto md:mr-0">
              <span className="text-sm font-semibold uppercase tracking-wide text-brand-600">About Us</span>
              <div className="mt-2 h-1 w-12 rounded bg-brand-500" />
              <div className="mt-6 space-y-4 text-slate-700">
                <p>
                  At <strong className="text-slate-900">UCLIPSE Detergents &amp; Chemicals</strong>, we believe that
                  professional cleaning begins with superior formulation. As a manufacturer of industrial and
                  institutional cleaning solutions, we combine advanced chemistry, stringent quality standards, and
                  reliable manufacturing processes to create products that perform where it matters most.
                </p>
                <p>
                  From hospitality and healthcare to corporate workplaces and industrial facilities, our solutions are
                  trusted to deliver outstanding cleanliness, hygiene, and long-lasting value.
                </p>
              </div>
              <figure className="mt-8 overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200">
                <img
                  src="/home/lab.jpg"
                  alt="UCLIPSE in-house laboratory with certified, scientifically tested cleaning formulations"
                  loading="lazy"
                  className="h-56 w-full object-cover"
                />
                <figcaption className="bg-white px-4 py-2 text-xs font-medium text-slate-500">
                  In-house R&amp;D &amp; quality-control laboratory
                </figcaption>
              </figure>
            </div>
          </div>
          <div className="relative overflow-hidden bg-brand-800 px-4 py-14 text-white sm:px-6 md:py-20 lg:px-12">
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-500/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 right-0 h-64 w-64 rounded-full bg-brand-400/20 blur-3xl" />
            <div className="relative mx-auto max-w-xl md:ml-0">
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                Why choose <span className="text-brand-200">UCLIPSE?</span>
              </h2>
              <div className="mt-2 h-1 w-12 rounded bg-brand-300" />
              <ul className="mt-8 space-y-7">
                {whyChoose.map((w) => (
                  <li key={w.title} className="flex gap-4">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-brand-700 shadow-md">
                      <w.Icon className="h-6 w-6" />
                    </span>
                    <div>
                      <h3 className="font-display text-sm font-bold uppercase tracking-wide">{w.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-brand-50/80">{w.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Industries we serve */}
      <section className="container-page py-16">
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-sm font-semibold uppercase tracking-wide text-brand-600">Industries we serve</span>
          <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 md:text-4xl">
            Trusted across demanding environments
          </h2>
          <p className="mt-3 text-slate-600">
            From five-star hospitality to sterile healthcare, UCLIPSE keeps professional spaces spotless, safe, and
            inspection-ready.
          </p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {industries.map((it) => (
            <article
              key={it.title}
              className="group overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-slate-100"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={it.img}
                  alt={it.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-900/75 via-brand-900/10 to-transparent" />
                <h3 className="absolute inset-x-0 bottom-0 p-4 font-display text-base font-bold text-white">
                  {it.title}
                </h3>
              </div>
              <p className="px-4 py-3 text-sm text-slate-600">{it.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* The Uclipse Standard — enterprise pillars */}
      <section className="relative overflow-hidden bg-slate-950 py-16 text-white md:py-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              'radial-gradient(50% 70% at 85% 0%, rgba(14,91,168,0.5) 0%, rgba(2,6,23,0) 60%), radial-gradient(45% 55% at 0% 100%, rgba(123,193,66,0.14) 0%, rgba(2,6,23,0) 60%)',
          }}
        />
        <div className="container-page relative">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-300">
              The Uclipse Standard
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold md:text-4xl">
              Beyond a supplier — a premium enterprise partner
            </h2>
            <p className="mt-3 text-slate-300">
              Four commitments that separate Uclipse from transactional chemical vendors and turn cleaning into an
              operational advantage.
            </p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {enterprisePillars.map((p) => (
              <Link
                key={p.id}
                to={p.route}
                className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-brand-400/50 hover:bg-white/[0.06]"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-500/15 text-brand-300 ring-1 ring-brand-400/20">
                  <PillarIcon icon={p.icon} className="h-6 w-6" />
                </span>
                <div className="mt-4 text-[0.7rem] font-semibold uppercase tracking-wide text-brand-300/80">
                  {p.eyebrow}
                </div>
                <h3 className="mt-1 font-display text-base font-semibold leading-snug">{p.name}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{p.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-300 group-hover:gap-2">
                  Explore <span aria-hidden>→</span>
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-9">
            <Link to="/enterprise" className="btn bg-white text-slate-900 hover:bg-slate-100">
              Discover the Uclipse Standard →
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="container-page py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">
            Built for every cleaning need
          </h2>
          <p className="mt-3 text-slate-600">
            From bathroom hygiene to automotive shine — one trusted manufacturer for your full housekeeping
            chemical line.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
          {categories.slice(0, 10).map((c) => (
            <Link
              key={c.slug}
              to={`/products?category=${c.slug}`}
              className="card p-5 text-center transition-shadow hover:shadow-lg"
            >
              <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-lg bg-brand-50 text-brand-600">
                <span className="font-display font-bold">{c.name.charAt(0)}</span>
              </div>
              <div className="text-sm font-semibold text-slate-900">{c.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-slate-50 py-16">
        <div className="container-page">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <h2 className="font-display text-3xl font-bold text-slate-900">Featured products</h2>
              <p className="mt-2 text-slate-600">Best-sellers across institutional buyers.</p>
            </div>
            <Link to="/products" className="hidden text-sm font-semibold text-brand-600 hover:underline sm:inline">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
            {showcase.map((p) => (
              <ProductCard key={`${p.partitionKey}-${p.rowKey}`} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* B2B value props */}
      <section className="container-page py-16">
        <div className="grid gap-8 md:grid-cols-3">
          {[
            {
              title: 'High-yield concentrates',
              body: 'Up to 1:60 dilution ratios slash per-litre cost vs ready-to-use cleaners.',
            },
            {
              title: 'Bulk-pack ready',
              body: '5L jerry cans standard; 20L / 50L drums available on volume orders.',
            },
            {
              title: 'Export-ready',
              body: 'Bulk packs, datasheets, and documentation prepared for domestic distribution and export markets.',
            },
          ].map((v) => (
            <div key={v.title} className="card p-6">
              <div className="mb-3 grid h-10 w-10 place-items-center rounded-lg bg-accent-green/10 text-accent-green">
                ✓
              </div>
              <h3 className="font-display text-lg font-semibold">{v.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-700 py-16 text-white">
        <div className="container-page text-center">
          <h2 className="font-display text-3xl font-bold md:text-4xl">Ready to scale your cleaning supply?</h2>
          <p className="mx-auto mt-3 max-w-2xl text-brand-50/90">
            Get a custom price list for your facility or apply to become a regional distributor.
          </p>
          <div className="mt-7 flex justify-center gap-3">
            <Link to="/contact" className="btn bg-white text-brand-700 hover:bg-slate-100">
              Request a Quote
            </Link>
            <Link
              to="/distributors"
              className="btn bg-accent-green text-white hover:bg-green-600"
            >
              Distribute Uclipse
            </Link>
          </div>
        </div>
      </section>

      {/* Tagline strip */}
      <section className="bg-brand-900 py-8">
        <div className="container-page text-center">
          <p className="font-display text-base font-semibold uppercase tracking-[0.18em] text-white sm:text-xl sm:tracking-[0.22em]">
            Cleaner spaces. <span className="text-brand-300">Safer environments.</span> Better future.
          </p>
        </div>
      </section>
    </>
  );
}
